import React, { useState, useEffect } from "react";
import { Card, Row, Col, Form, InputGroup, Spinner } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { var_api } from "../../../constant";

const Reports = () => {
  const itemsPerPage = 15;

  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [grandTotal, setGrandTotal] = useState(0);

  // 🔹 Default dates
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 30);

  const [fromDate, setFromDate] = useState(firstDayOfMonth);
  const [toDate, setToDate] = useState(lastDayOfMonth);

  // 🔹 Fetch Report Data
  const handleGenerateList = async (from, to) => {
    if (!from || !to) {
      setErrors({ dateRange: "Please select both From and To dates" });
      toast.warning("Please select both From and To dates");
      return;
    }

    if (from > to) {
      setErrors({ dateRange: "From date cannot be after To date" });
      toast.warning("From date cannot be after To date");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        warehouse_id: localStorage.getItem("warehouse_id"),
        from_date: from,
        to_date: to,
      };

      const res = await axios.get(`${var_api}veemart_instore_pos_billing/paymode-report`, {
        params: payload,
      });

      if (res.data && Array.isArray(res.data.report)) {
        let filtered = res.data.report;

        if (searchQuery.trim()) {
          filtered = filtered.filter((item) =>
            item.payment_mode?.toLowerCase().includes(searchQuery.trim().toLowerCase())
          );
        }

        setReportData(res.data.report);
        setFilteredData(filtered);
        setGrandTotal(res.data.grand_total || 0);
        setCurrentPage(1);
        setErrors({});
      } else {
        setReportData([]);
        setFilteredData([]);
        setGrandTotal(0);
      }
    } catch (error) {
      toast.error("Error fetching report");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Auto-fetch on first load
  useEffect(() => {
    handleGenerateList(fromDate, toDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔹 Auto-refresh when date range changes
  useEffect(() => {
    if (fromDate && toDate) {
      handleGenerateList(fromDate, toDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  // 🔹 Auto-filter when search text changes
  useEffect(() => {
    if (searchQuery.trim()) {
      setFilteredData(
        reportData.filter((item) =>
          item.payment_mode?.toLowerCase().includes(searchQuery.trim().toLowerCase())
        )
      );
    } else {
      setFilteredData(reportData);
    }
  }, [searchQuery, reportData]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <>
      {/* 🔹 Full Page Loader */}
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <div className="text-center">
            <Spinner animation="border" variant="primary" style={{ width: "3rem", height: "3rem" }} />
            <div className="mt-3 fw-bold">Loading Reports...</div>
          </div>
        </div>
      )}

      <Card className="shadow-sm">
        <Card.Body>
          <h4>
            <i className="bi bi-file-earmark-text me-2"></i> Payment Mode Report
          </h4>

          <Row className="align-items-end mb-3">
            <Col md={6}>
              <Form.Label className="fw-bold">
                <h5>Select Date Range</h5>
              </Form.Label>
              <InputGroup className="gap-2">
                <DatePicker
                  className="form-control"
                  selected={fromDate}
                  onChange={(date) => setFromDate(date)}
                  selectsStart
                  startDate={fromDate}
                  endDate={toDate}
                  placeholderText="From Date"
                  dateFormat="dd/MM/yyyy"
                />
                <DatePicker
                  className="form-control"
                  selected={toDate}
                  onChange={(date) => setToDate(date)}
                  selectsEnd
                  startDate={fromDate}
                  endDate={toDate}
                  minDate={fromDate}
                  placeholderText="To Date"
                  dateFormat="dd/MM/yyyy"
                />
              </InputGroup>
            </Col>

            <Col md={3} className="ms-auto">
              <Form.Control
                type="text"
                placeholder="Search by Payment Mode..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Col>
          </Row>

          {errors.dateRange && (
            <div className="text-danger mb-2">{errors.dateRange}</div>
          )}

          {/* Table */}
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Payment Mode</th>
                  <th>Total Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="text-center text-muted py-3">
                      No data found
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item, idx) => (
                    <tr key={idx}>
                      <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      <td>{item.payment_mode || "-"}</td>
                      <td>
                        <span className="badge bg-success fs-6">
                          ₹ {Number(item.total_amount).toLocaleString("en-IN")}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* 🔹 Grand Total */}
          <div className="text-end mt-3 fw-bold fs-5">
            Grand Total:{" "}
            <span className="text-success">
              ₹ {grandTotal.toLocaleString("en-IN")}
            </span>
          </div>
        </Card.Body>
      </Card>

      <ToastContainer />
    </>
  );
};

export default Reports;
